import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Search, Assessment, CreditCard, AccountBalanceWallet, LocalShipping } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { useDadosPedidoFornecedorContext } from "../context/useDadosPedidoFornecedorContext"
import { DadosPedidoFornecedorActionsTypes } from "../context/dadosPedidoFornecedorActions"
import type { PedidoViewItem } from "../models/dadosPedidoFornecedorModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "1200px",
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    botaoPesquisar: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: "10px 30px",
        fontWeight: 600,
        "&:hover": { backgroundColor: "#388e3c" },
        boxShadow: "0 4px 15px rgba(76,175,80,0.3)",
    },
    resumoCard: {
        borderRadius: 12,
        padding: theme.spacing(2),
        backgroundColor: "#f8f9fa",
        marginBottom: theme.spacing(1),
    },
    resumoLabel: {
        color: "#7f8c8d",
        fontSize: "0.85rem",
    },
    resumoValor: {
        color: "#2c3e50",
        fontWeight: 600,
        fontSize: "1rem",
    },
    tableHeader: {
        backgroundColor: "#f8f9fa",
        "& th": {
            fontWeight: 600,
            color: "#2c3e50",
            fontSize: "0.8rem",
        },
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 150,
    },
    statusCancelado: { color: "#e74c3c", fontWeight: 600 },
    statusConfirmado: { color: "#27ae60", fontWeight: 600 },
    statusNormal: { color: "#2c3e50" },
}))

function formatarMoeda(valor: number | null | undefined): string {
    if (valor == null) return "R$ 0,00"
    return "R$ " + valor.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

function formatarData(timestamp: number): string {
    const d = new Date(timestamp)
    const dd = String(d.getDate()).padStart(2, "0")
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const yyyy = d.getFullYear()
    const hh = String(d.getHours()).padStart(2, "0")
    const min = String(d.getMinutes()).padStart(2, "0")
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`
}

export function ComponentePrincipal() {
    const classes = useStyles()
    const { dados, dispatch, pesquisar, erroCarregamento } = useDadosPedidoFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()
    const { filtro, resultado, pagina, itensPorPagina } = dados

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleFiltroChange = (campo: string, valor: string | boolean) => {
        dispatch({
            type: DadosPedidoFornecedorActionsTypes.DEFINIR_FILTRO,
            payload: { [campo]: valor },
        })
    }

    const handlePesquisar = async () => {
        if (!filtro.dataInicio || !filtro.dataFim) {
            alertStart("Informe o período de busca.", "error")
            return
        }
        await pesquisar()
    }

    const pedidos = resultado?.pedidos ?? []
    const pedidosPaginados = pedidos.slice(pagina * itensPorPagina, (pagina + 1) * itensPorPagina)

    const getStatusClass = (status: string) => {
        if (status.toLowerCase().includes("cancel")) return classes.statusCancelado
        if (["Aceito", "Sendo Preparado", "Saiu para Entrega", "Entregue", "Pago", "Concluído"].includes(status)) return classes.statusConfirmado
        return classes.statusNormal
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                {/* Filtros */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Search className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtros
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Data Início"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder="dd/mm/aaaa"
                                    value={filtro.dataInicio}
                                    onChange={e => handleFiltroChange("dataInicio", e.target.value)}
                                    inputProps={{ maxLength: 10 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Hora Início"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder="HH:mm"
                                    value={filtro.horaInicio ?? ""}
                                    onChange={e => handleFiltroChange("horaInicio", e.target.value)}
                                    inputProps={{ maxLength: 5 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Data Fim"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder="dd/mm/aaaa"
                                    value={filtro.dataFim}
                                    onChange={e => handleFiltroChange("dataFim", e.target.value)}
                                    inputProps={{ maxLength: 10 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Hora Fim"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder="HH:mm"
                                    value={filtro.horaFim ?? ""}
                                    onChange={e => handleFiltroChange("horaFim", e.target.value)}
                                    inputProps={{ maxLength: 5 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Código do Pedido"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.codigo ?? ""}
                                    onChange={e => handleFiltroChange("codigo", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Cupom de Desconto"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.cupomDesconto ?? ""}
                                    onChange={e => handleFiltroChange("cupomDesconto", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Plataforma"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.plataforma ?? ""}
                                    onChange={e => handleFiltroChange("plataforma", e.target.value)}
                                >
                                    <MenuItem value="">Todas as plataformas</MenuItem>
                                    <MenuItem value="aplicativo">Delivery Próprio</MenuItem>
                                    <MenuItem value="Central de Pedidos">Central de Pedidos</MenuItem>
                                    <MenuItem value="IFood">IFood</MenuItem>
                                    <MenuItem value="99 Food">99 Food</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Situação"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.status ?? ""}
                                    onChange={e => handleFiltroChange("status", e.target.value)}
                                >
                                    <MenuItem value="">Todas as situações</MenuItem>
                                    <MenuItem value="777">Todos os confirmados</MenuItem>
                                    <MenuItem value="-777">Todos os cancelados</MenuItem>
                                    <MenuItem value="-2">Cancelado Cliente</MenuItem>
                                    <MenuItem value="-1">Cancelado Estabelecimento</MenuItem>
                                    <MenuItem value="1">Aceito</MenuItem>
                                    <MenuItem value="7">Sendo Preparado</MenuItem>
                                    <MenuItem value="3">Saiu para Entrega</MenuItem>
                                    <MenuItem value="4">Entregue</MenuItem>
                                    <MenuItem value="5">Pago</MenuItem>
                                    <MenuItem value="6">Pronto Para Retirada</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Situação do Pagamento"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.statusPagamento ?? ""}
                                    onChange={e => handleFiltroChange("statusPagamento", e.target.value)}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    <MenuItem value="5">Pago Online</MenuItem>
                                    <MenuItem value="-8">Estornada</MenuItem>
                                    <MenuItem value="-10">Chargeback</MenuItem>
                                    <MenuItem value="-5">Falha Pagamento</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="E-mail do cliente"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.emailCliente ?? ""}
                                    onChange={e => handleFiltroChange("emailCliente", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Nome do cliente"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.nomeCliente ?? ""}
                                    onChange={e => handleFiltroChange("nomeCliente", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Forma de Entrega"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.tipoPedido ?? ""}
                                    onChange={e => handleFiltroChange("tipoPedido", e.target.value)}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    <MenuItem value="0">Delivery</MenuItem>
                                    <MenuItem value="1">Retirada no Local</MenuItem>
                                    <MenuItem value="2">Consumo no Local/Mesa</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label="Tipo da Forma de Pagamento"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtro.grupoFormaPagamento ?? ""}
                                    onChange={e => handleFiltroChange("grupoFormaPagamento", e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="CARD">Cartão</MenuItem>
                                    <MenuItem value="DIN">Dinheiro</MenuItem>
                                    <MenuItem value="PIX">Pix</MenuItem>
                                    <MenuItem value="VOUCHER">Voucher</MenuItem>
                                    <MenuItem value="VALE">Vale Refeição</MenuItem>
                                    <MenuItem value="OTHER">Outros</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filtro.exibirPedidosNaoConfirmados ?? false}
                                            onChange={e => handleFiltroChange("exibirPedidosNaoConfirmados", e.target.checked)}
                                            style={{ color: "#4caf50" }}
                                        />
                                    }
                                    label="Exibir pedidos não aprovados"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filtro.exibirExpirados ?? false}
                                            onChange={e => handleFiltroChange("exibirExpirados", e.target.checked)}
                                            style={{ color: "#4caf50" }}
                                        />
                                    }
                                    label="Exibir pedidos expirados"
                                />
                            </Grid>
                            <Grid item xs={12} style={{ textAlign: "center" }}>
                                <Button
                                    variant="contained"
                                    className={classes.botaoPesquisar}
                                    onClick={handlePesquisar}
                                    startIcon={<Search />}
                                >
                                    Pesquisar
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Tabela de Pedidos */}
                {resultado && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Assessment className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Pedidos ({resultado.totalPedidos} registro(s))
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent} style={{ padding: 0 }}>
                            <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                                <Table size="small" stickyHeader style={{ minWidth: 900, width: "100%" }}>
                                    <TableHead className={classes.tableHeader}>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 80 }}>Cód.</TableCell>
                                            <TableCell style={{ minWidth: 140 }}>Cliente</TableCell>
                                            <TableCell style={{ minWidth: 110 }}>Forma Pgto</TableCell>
                                            <TableCell style={{ minWidth: 90 }}>Valor</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Data/Hora</TableCell>
                                            <TableCell style={{ minWidth: 130 }}>Situação</TableCell>
                                            <TableCell style={{ minWidth: 110 }}>Plataforma</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Entregador</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pedidosPaginados.map((pedido: PedidoViewItem) => (
                                            <TableRow key={pedido.id} hover>
                                                <TableCell>
                                                    <a
                                                        href={pedido.linkPedido}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{ color: "#4caf50" }}
                                                    >
                                                        {pedido.id}
                                                    </a>
                                                </TableCell>
                                                <TableCell className={classes.nomeCell} title={pedido.cliente}>
                                                    {pedido.cliente}
                                                </TableCell>
                                                <TableCell className={classes.nomeCell} title={pedido.formaPagamento ?? ""}>
                                                    {pedido.formaPagamento}
                                                    {pedido.gateway && (
                                                        <Typography
                                                            component="span"
                                                            style={{ fontSize: "0.7rem", color: "blueviolet", marginLeft: 4 }}
                                                        >
                                                            ({pedido.gateway})
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>{formatarMoeda(pedido.valorTotal)}</TableCell>
                                                <TableCell>{formatarData(pedido.dataRealizacao)}</TableCell>
                                                <TableCell>
                                                    <Typography
                                                        component="span"
                                                        className={getStatusClass(pedido.status)}
                                                        style={{ fontSize: "0.8rem" }}
                                                    >
                                                        {pedido.status}
                                                    </Typography>
                                                    {pedido.statusPagamento === "Chargeback" && (
                                                        <Typography
                                                            component="span"
                                                            style={{ fontSize: "0.7rem", color: "#fb0000", display: "block" }}
                                                        >
                                                            Chargeback
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell className={classes.nomeCell} title={pedido.plataforma}>
                                                    {pedido.plataforma}
                                                </TableCell>
                                                <TableCell className={classes.nomeCell} title={pedido.motoboyPedido ?? ""}>
                                                    {pedido.motoboyPedido}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {pedidosPaginados.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" style={{ color: "#7f8c8d", padding: 24 }}>
                                                    Nenhum pedido encontrado.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={pedidos.length}
                                page={pagina}
                                onPageChange={(_, p) =>
                                    dispatch({ type: DadosPedidoFornecedorActionsTypes.SET_PAGINA, payload: p })
                                }
                                rowsPerPage={itensPorPagina}
                                onRowsPerPageChange={() => undefined}
                                rowsPerPageOptions={[100]}
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                            />
                        </Box>
                    </Paper>
                )}

                {/* Resumo Financeiro */}
                {resultado && (
                    <Grid container spacing={3}>
                        {/* Resumo Geral */}
                        <Grid item xs={12} md={4}>
                            <Paper className={classes.configCard}>
                                <Box className={classes.cardHeader}>
                                    <Assessment className={classes.iconHeader} />
                                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                                        Resumo
                                    </Typography>
                                </Box>
                                <Box className={classes.cardContent}>
                                    {[
                                        ["(+) Valor bruto", formatarMoeda(resultado.resumo.valorBruto)],
                                        ["(-) Descontos", formatarMoeda(resultado.resumo.descontos)],
                                        ["(=) Valor líquido", formatarMoeda(resultado.resumo.liquido)],
                                        ["(%) Percentual descontos", resultado.resumo.percentualDesconto.toFixed(1) + "%"],
                                        ["Total taxas entrega", formatarMoeda(resultado.resumo.taxasEntrega)],
                                        [`Cancelados/Expirados (${resultado.resumo.qtdCancelados})`, formatarMoeda(resultado.resumo.perdidos)],
                                    ].map(([label, valor]) => (
                                        <Box key={label} display="flex" justifyContent="space-between" mb={0.5}>
                                            <Typography className={classes.resumoLabel}>{label}</Typography>
                                            <Typography className={classes.resumoValor}>{valor}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Cartão Online */}
                        {resultado.cartaoOnline.pagamentos > 0 && (
                            <Grid item xs={12} md={4}>
                                <Paper className={classes.configCard}>
                                    <Box className={classes.cardHeader}>
                                        <CreditCard className={classes.iconHeader} />
                                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                                            Cartão Online
                                        </Typography>
                                    </Box>
                                    <Box className={classes.cardContent}>
                                        {[
                                            ["Pagamentos online", formatarMoeda(resultado.cartaoOnline.pagamentos)],
                                            ["Taxa", resultado.cartaoOnline.taxa.toFixed(2) + "%"],
                                            [`Chargeback (${resultado.cartaoOnline.chargeBackPercentual.toFixed(1)}%)`, formatarMoeda(resultado.cartaoOnline.chargeback)],
                                            ["Saldo", formatarMoeda(resultado.cartaoOnline.saldo)],
                                        ].map(([label, valor]) => (
                                            <Box key={label} display="flex" justifyContent="space-between" mb={0.5}>
                                                <Typography className={classes.resumoLabel}>{label}</Typography>
                                                <Typography className={classes.resumoValor}>{valor}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                        )}

                        {/* Pix Online */}
                        <Grid item xs={12} md={4}>
                            <Paper className={classes.configCard}>
                                <Box className={classes.cardHeader}>
                                    <AccountBalanceWallet className={classes.iconHeader} />
                                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                                        Pix Online
                                    </Typography>
                                </Box>
                                <Box className={classes.cardContent}>
                                    {[
                                        ["Qtd. vendas Pix", String(resultado.pixOnline.qtd)],
                                        ["Valor vendas Pix", formatarMoeda(resultado.pixOnline.valor)],
                                        ["Taxas Pix", formatarMoeda(resultado.pixOnline.taxas)],
                                        ["Líquido Pix", formatarMoeda(resultado.pixOnline.liquido)],
                                    ].map(([label, valor]) => (
                                        <Box key={label} display="flex" justifyContent="space-between" mb={0.5}>
                                            <Typography className={classes.resumoLabel}>{label}</Typography>
                                            <Typography className={classes.resumoValor}>{valor}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Formas de Pagamento */}
                        {resultado.formasPagamento.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Paper className={classes.configCard}>
                                    <Box className={classes.cardHeader}>
                                        <AccountBalanceWallet className={classes.iconHeader} />
                                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                                            Formas de Pagamento
                                        </Typography>
                                    </Box>
                                    <Box className={classes.cardContent}>
                                        {resultado.formasPagamento.map(fp => (
                                            <Box key={fp.nome} display="flex" justifyContent="space-between" mb={0.5}>
                                                <Typography className={classes.resumoLabel}>
                                                    {fp.nome} ({fp.qtd} pedidos)
                                                </Typography>
                                                <Typography className={classes.resumoValor}>
                                                    {formatarMoeda(fp.valor)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                        )}

                        {/* Entregadores */}
                        {resultado.entregadores.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Paper className={classes.configCard}>
                                    <Box className={classes.cardHeader}>
                                        <LocalShipping className={classes.iconHeader} />
                                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                                            Resumo Entregas ({resultado.resumo.qtdTotalEntregas} pedidos)
                                        </Typography>
                                    </Box>
                                    <Box className={classes.cardContent}>
                                        <TableContainer style={{ overflowX: "auto" }}>
                                            <Table size="small" style={{ minWidth: 600, width: "100%" }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell style={{ minWidth: 180, fontWeight: 600, color: "#2c3e50" }}>Entregador</TableCell>
                                                        <TableCell style={{ minWidth: 110, fontWeight: 600, color: "#2c3e50" }}>Vr. Total</TableCell>
                                                        <TableCell style={{ minWidth: 90, fontWeight: 600, color: "#2c3e50" }}>Qtd.</TableCell>
                                                        <TableCell style={{ minWidth: 110, fontWeight: 600, color: "#2c3e50" }}>Dinheiro</TableCell>
                                                        <TableCell style={{ minWidth: 110, fontWeight: 600, color: "#2c3e50" }}>Cartão</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {resultado.entregadores.map(ent => (
                                                        <TableRow key={ent.nome}>
                                                            <TableCell style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {ent.nome}
                                                            </TableCell>
                                                            <TableCell>{formatarMoeda(ent.vrTotal)}</TableCell>
                                                            <TableCell>{ent.qtdEntregas}</TableCell>
                                                            <TableCell>{formatarMoeda(ent.vrDinheiro)}</TableCell>
                                                            <TableCell>{formatarMoeda(ent.vrCartao)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Box>
        </Box>
    )
}
